import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * JitsiCallContext
 *
 * Holds the active call state globally so the floating JitsiMeetPanel
 * survives route/page changes.
 *
 * Usage:
 *   const { startCall, endCall, isCallActive } = useJitsiCall();
 *   startCall({ classroomId, displayName, isCoach });
 *
 * Coach-only:
 *   broadcastCallState POSTs to the backend which uses the Admin SDK to write
 *   videoCallActive to Firestore — client writes are blocked by Firestore rules.
 */
const JitsiCallContext = createContext(null);

async function broadcastCallState(classroomId, active) {
    if (!classroomId) return;
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5025/api'}/jaas/call-state`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ classroomId, active }),
            }
        );
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            console.error('[JitsiCallContext] broadcastCallState failed:', res.status, body);
        }
    } catch (e) {
        console.error('[JitsiCallContext] broadcastCallState threw:', e);
    }
}

export function JitsiCallProvider({ children }) {
    const [callConfig, setCallConfig] = useState(null);

    const startCall = useCallback(({ classroomId, displayName, isCoach }) => {
        setCallConfig({ classroomId, displayName, isCoach });
        if (isCoach) broadcastCallState(classroomId, true);
    }, []);

    const endCall = useCallback(() => {
        setCallConfig((prev) => {
            if (prev?.isCoach) broadcastCallState(prev.classroomId, false);
            return null;
        });
    }, []);

    return (
        <JitsiCallContext.Provider value={{
            callConfig,
            isCallActive: !!callConfig,
            startCall,
            endCall,
        }}>
            {children}
        </JitsiCallContext.Provider>
    );
}

export function useJitsiCall() {
    const ctx = useContext(JitsiCallContext);
    if (!ctx) throw new Error('useJitsiCall must be used inside JitsiCallProvider');
    return ctx;
}
